import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

interface Step {
  number: number;
  description: string;
  status: 'pending' | 'running' | 'success' | 'error';
  details?: string;
  timestamp?: number;
}

export const AIIntegrationDebugger: React.FC = () => {
  const { token } = useAuth();
  const [goalInput, setGoalInput] = useState('');
  const [aiModel, setAiModel] = useState('@cf/meta/llama-3-8b-instruct');
  const [steps, setSteps] = useState<Step[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiResponse, setAiResponse] = useState<any>(null);

  const initialSteps: Step[] = [
    // Client-side preparation
    { number: 1, description: "User enters goal/prompt", status: 'pending' },
    { number: 2, description: "Validate input (non-empty, length check)", status: 'pending' },
    { number: 3, description: "Check authentication token", status: 'pending' },
    { number: 4, description: "Build AI request payload", status: 'pending' },
    { number: 5, description: "Send POST to /api/ai/generate", status: 'pending' },

    // Server-side processing
    { number: 6, description: "Worker receives request", status: 'pending' },
    { number: 7, description: "Validate Authorization header", status: 'pending' },
    { number: 8, description: "Parse and validate request body", status: 'pending' },
    { number: 9, description: "Call CloudFlare AI binding", status: 'pending' },
    { number: 10, description: "AI model processes prompt", status: 'pending' },
    { number: 11, description: "Receive AI response", status: 'pending' },
    { number: 12, description: "Store in D1 database", status: 'pending' },
    { number: 13, description: "Format and return response", status: 'pending' },

    // Client-side completion
    { number: 14, description: "Client receives AI response", status: 'pending' },
    { number: 15, description: "Update UI with results", status: 'pending' },
  ];

  const updateStep = (stepNumber: number, status: 'running' | 'success' | 'error', details?: string) => {
    setSteps(prev => prev.map(step =>
      step.number === stepNumber
        ? { ...step, status, details, timestamp: Date.now() }
        : step
    ));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setGoalInput(value);

    if (value.length > 0) {
      setSteps(initialSteps);
      updateStep(1, 'success', `${value.length} characters entered`);
    }
  };

  const handleSubmit = async () => {
    if (!goalInput.trim()) {
      alert('Please enter a goal or prompt');
      return;
    }

    setIsProcessing(true);
    setAiResponse(null);

    // Step 1: Already completed when typing
    if (steps[0].status !== 'success') {
      updateStep(1, 'success', 'Goal entered');
    }
    await delay(200);

    // Step 2: Validate input
    updateStep(2, 'running', 'Validating input...');
    await delay(300);
    if (goalInput.length < 3) {
      updateStep(2, 'error', 'Input too short (min 3 chars)');
      setIsProcessing(false);
      return;
    }
    if (goalInput.length > 2000) {
      updateStep(2, 'error', 'Input too long (max 2000 chars)');
      setIsProcessing(false);
      return;
    }
    updateStep(2, 'success', `Valid: ${goalInput.length} chars`);

    // Step 3: Check token
    updateStep(3, 'running', 'Checking authentication...');
    await delay(300);
    if (!token) {
      updateStep(3, 'error', 'NO AUTH TOKEN! Please log in.');
      setIsProcessing(false);
      return;
    }
    updateStep(3, 'success', `Token: ${token.substring(0, 20)}...`);

    // Step 4: Build payload
    updateStep(4, 'running', 'Building request payload...');
    await delay(200);
    const payload = {
      prompt: goalInput,
      model: aiModel,
      context: 'goal_achievement',
      max_tokens: 500,
      temperature: 0.7,
      timestamp: Date.now()
    };
    updateStep(4, 'success', `Payload ready (${JSON.stringify(payload).length} bytes)`);

    // Step 5: Send request
    updateStep(5, 'running', 'Sending to API...');
    const apiUrl = buildApiUrl('/ai/generate');

    try {
      console.log('🚀 Sending AI request to:', apiUrl);
      console.log('📦 Request payload:', payload);
      console.log('🔑 Auth header:', `Bearer ${token.substring(0, 20)}...`);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      updateStep(5, 'success', `Response: ${response.status} ${response.statusText}`);

      if (response.ok) {
        // Server-side steps (simulated based on response)
        updateStep(6, 'success', 'Worker received request');
        await delay(100);
        updateStep(7, 'success', 'Authorization verified');
        await delay(100);
        updateStep(8, 'success', 'Request body validated');
        await delay(100);
        updateStep(9, 'running', 'Calling CloudFlare AI...');
        await delay(500);
        updateStep(9, 'success', `Model: ${aiModel}`);
        updateStep(10, 'running', 'AI processing prompt...');
        await delay(1000);
        updateStep(10, 'success', 'AI processing complete');
        updateStep(11, 'success', 'AI response received');
        await delay(100);
        updateStep(12, 'success', 'Stored in database');
        await delay(100);
        updateStep(13, 'success', 'Response formatted');

        // Client-side completion
        const data = await response.json();
        updateStep(14, 'success', 'Response received');

        setAiResponse(data);
        updateStep(15, 'success', '🎉 UI UPDATED WITH AI RESPONSE!');

        console.log('✅ AI Integration successful:', data);
      } else {
        const errorData = await response.json();
        console.error('❌ AI request failed:', errorData);

        // Determine failure point
        if (response.status === 401) {
          updateStep(6, 'success', 'Worker received request');
          updateStep(7, 'error', 'Authentication failed');
        } else if (response.status === 400) {
          updateStep(6, 'success', 'Worker received request');
          updateStep(7, 'success', 'Auth verified');
          updateStep(8, 'error', `Invalid request: ${errorData.error}`);
        } else if (response.status === 500) {
          updateStep(6, 'success', 'Worker received request');
          updateStep(7, 'success', 'Auth verified');
          updateStep(8, 'success', 'Request valid');
          updateStep(9, 'error', `AI service error: ${errorData.error}`);
        } else {
          updateStep(6, 'error', `Server error: ${errorData.error}`);
        }
      }
    } catch (error) {
      console.error('💥 Network error:', error);
      updateStep(5, 'error', `Network error: ${error instanceof Error ? error.message : 'Unknown'}`);
    }

    setIsProcessing(false);
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'running': return 'text-teal-600 bg-teal-50 animate-pulse';
      default: return 'text-gray-400 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'running': return '⚡';
      default: return '⏳';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-orange-100 p-6">
      <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-orange-500 to-teal-500 bg-clip-text text-transparent">
        🤖 AI Integration Debugger
      </h2>

      {/* Input Section */}
      <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700">
            Enter your goal or prompt:
          </label>
          <textarea
            value={goalInput}
            onChange={handleInputChange}
            placeholder="e.g., I want to learn Spanish in 3 months..."
            className="w-full p-3 rounded-lg bg-white text-gray-800 placeholder-gray-400
                     border border-orange-300 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200
                     resize-none h-24"
            maxLength={2000}
          />
          <div className="text-xs text-gray-400 mt-1">
            {goalInput.length}/2000 characters
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-300">
            AI Model:
          </label>
          <select
            value={aiModel}
            onChange={(e) => setAiModel(e.target.value)}
            className="w-full p-2 rounded-lg bg-white text-gray-800 border border-orange-300
                     focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
          >
            <option value="@cf/meta/llama-3-8b-instruct">Llama 3 8B Instruct</option>
            <option value="@cf/mistral/mistral-7b-instruct-v0.1">Mistral 7B</option>
            <option value="@cf/microsoft/phi-2">Phi-2</option>
          </select>
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!goalInput.trim() || isProcessing}
        className={`mb-6 px-8 py-4 rounded-lg font-bold text-lg text-white transition-all transform
          ${!goalInput.trim() || isProcessing
            ? 'bg-gray-400 cursor-not-allowed opacity-50'
            : 'bg-gradient-to-r from-orange-500 to-teal-500 hover:from-orange-600 hover:to-teal-600 hover:scale-105 shadow-lg'
          }`}
      >
        {isProcessing ? '⚡ Processing AI Request...' : '🚀 Send to CloudFlare AI'}
      </button>

      {/* Steps Timeline */}
      <div className="space-y-2 mb-6">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`flex items-start gap-3 p-3 rounded-lg transition-all ${getStatusColor(step.status)}`}
          >
            <div className="flex-shrink-0 w-8 text-center font-bold">
              {step.number}
            </div>
            <div className="flex-shrink-0 w-6">
              {getStatusIcon(step.status)}
            </div>
            <div className="flex-grow">
              <div className="font-medium">{step.description}</div>
              {step.details && (
                <div className="text-sm mt-1 opacity-75">{step.details}</div>
              )}
            </div>
            {step.timestamp && steps[0]?.timestamp && (
              <div className="text-xs opacity-50">
                +{((step.timestamp - steps[0].timestamp) / 1000).toFixed(2)}s
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI Response Display */}
      {aiResponse && (
        <div className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <h3 className="text-lg font-bold mb-2 text-green-400">🎯 AI Response:</h3>
          <div className="text-sm text-gray-300 whitespace-pre-wrap">
            {aiResponse.response || aiResponse.text || JSON.stringify(aiResponse, null, 2)}
          </div>
        </div>
      )}

      {/* Console Output */}
      <div className="mt-6 p-4 bg-gray-900 rounded-lg font-mono text-xs text-green-400">
        <div className="text-orange-400">═══ Console Output ═══</div>
        <div>Ready: Check browser DevTools for detailed logs</div>
        {token && <div>✓ Auth Token: Present</div>}
        {goalInput && <div>✓ Goal: {goalInput.substring(0, 50)}...</div>}
        <div>✓ AI Model: {aiModel}</div>
        <div>✓ API Endpoint: {buildApiUrl('/ai/generate')}</div>
      </div>
    </div>
  );
};